import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui';
import { useAuth } from '../../contexts/AuthContext';
import { api, type CeqaBudgetPatch, type CeqaStatusResponse } from '../../lib/api';
import {
  CAP_LABEL, asSentence, checkpointConfirm, loadLine, nightlyLine, parseCapInput, usd, type CapKey,
} from './developmentLane';

/**
 * The new-development lane's header (spec 7.2, 7.3, 9.4): what the reading costs, what the load is doing, and
 * what could not be read.
 *
 * ⭐ D6, his words: "don't let the cost go too crazy ... let's keep an eye on it." So EVERYONE signed in sees the
 *   number; only an admin sees the controls - and the server refuses a non-admin anyway (`authorize(['admin'])`
 *   on every money route), so hiding them is courtesy, not the enforcement.
 * ⛔ Nothing here spends without a press, and "Start" says what month one costs before anything starts.
 */

const CAP_KEYS: CapKey[] = ['cap_load_usd', 'cap_night_usd', 'cap_30d_usd'];

/** The cap a paused load waits on, when a cap is what paused it. */
const capBehind = (reason: string | null | undefined): CapKey | null =>
  reason === 'cap_run' ? 'cap_load_usd' : reason === 'cap_30d' ? 'cap_30d_usd' : null;

export default function DevelopmentLaneHeader() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [status, setStatus] = useState<CeqaStatusResponse | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [caps, setCaps] = useState<Record<CapKey, string>>({ cap_load_usd: '', cap_night_usd: '', cap_30d_usd: '' });
  const [showUnreadable, setShowUnreadable] = useState(false);
  /** ⛔ RULING (N3): a status read is running - so a Refresh press shows it ran, even when it fails at once offline. */
  const [refreshing, setRefreshing] = useState(false);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const s = await api.getCeqaStatus();
      setStatus(s);
      setCaps({
        cap_load_usd: String(s.budget.cap_load_usd),
        cap_night_usd: String(s.budget.cap_night_usd),
        cap_30d_usd: String(s.budget.cap_30d_usd),
      });
      setStatusError(null);
    } catch (e) {
      // ⛔ RULING (I3): the last status that loaded STAYS - a failed refresh adds an error beside it, never blanks it.
      setStatusError(e instanceof Error ? e.message : 'Could not load the CEQA reader status');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  /** Every changed cap in one PATCH, each checked by the server's own rule first. False when refused. */
  const saveCaps = async (): Promise<boolean> => {
    if (!status) return false;
    const patch: CeqaBudgetPatch = {};
    for (const key of CAP_KEYS) {
      const parsed = parseCapInput(key, caps[key]);
      if (!parsed.ok) {
        setError(parsed.error);
        return false;
      }
      if (parsed.value !== status.budget[key]) patch[key] = parsed.value;
    }
    if (!Object.keys(patch).length) return true;
    setBusy('caps');
    setError(null);
    try {
      const { budget } = await api.updateCeqaBudget(patch);
      setStatus((s) => (s ? { ...s, budget } : s));
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the caps');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const setCheckpoint = async (on: boolean) => {
    setBusy('checkpoint');
    setError(null);
    try {
      const { budget } = await api.updateCeqaBudget({ checkpoint_first_month: on });
      setStatus((s) => (s ? { ...s, budget } : s));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the checkpoint');
    } finally {
      setBusy(null);
    }
  };

  const start = async () => {
    if (!status) return;
    // ⛔ It SPENDS, so the confirm names what before anything starts - and what it says is what happens: with
    //   the checkpoint off, the load reads straight through to its cap.
    const message = status.budget.checkpoint_first_month
      ? 'Start the 12-month CEQA load? It lands and screens all 12 months for free, then reads the newest month - about $1 - and stops at the checkpoint for your yes.'
      : `Start the 12-month CEQA load? The first-month checkpoint is OFF, so it reads all 12 months, up to the history load cap of ${usd(status.budget.cap_load_usd)}.`;
    if (!window.confirm(message)) return;
    setBusy('start');
    setError(null);
    try {
      await api.startCeqaLoad();
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the load');
    } finally {
      setBusy(null);
    }
  };

  /** Continue a paused or stalled load. ⛔ Past the checkpoint, this press IS his yes (spec 7.3). */
  const resume = async (raiseFirst: boolean) => {
    const run = status?.runs.load;
    if (!status || !run) return;
    /**
     * ⛔ F4 (the final review): at the checkpoint this press authorises the other 11 months, up to the history load cap -
     *   so, like Start, it asks first, in figures, and Cancel sends NOTHING (no caps, no continue). Here for either
     *   button: a raise saved first is the cap the load will read under, so the question names that one.
     */
    if (run.status === 'paused' && run.pause_reason === 'checkpoint') {
      const typed = parseCapInput('cap_load_usd', caps.cap_load_usd);
      const cap = raiseFirst && typed.ok ? typed.value : status.budget.cap_load_usd;
      if (!window.confirm(checkpointConfirm(run, cap))) return;
    }
    if (raiseFirst && !(await saveCaps())) return;
    setBusy('continue');
    setError(null);
    try {
      await api.continueCeqaLoad(run.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not continue the load');
    } finally {
      setBusy(null);
    }
  };

  if (!status) {
    if (!statusError) return <p className="mt-3 text-[13px] text-gray-500">Loading the CEQA reader...</p>;
    /**
     * Nothing has loaded yet: the server's words, said once (its sentence already names the CEQA reader status), and
     * a way to try again. ⛔ RULING (I3): offline, the iPhone app has no reload button - this is the only retry.
     */
    return (
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-900">
        <span data-testid="ceqa-header-error">{asSentence(statusError)}</span>
        <button type="button" onClick={() => void refresh()} data-testid="ceqa-refresh" disabled={refreshing}
          className="text-[12.5px] text-blue-700 hover:underline disabled:opacity-60">
          {refreshing ? 'Trying again...' : 'Refresh status'}
        </button>
      </div>
    );
  }

  const run = status.runs.load;
  const pausedOn = run?.status === 'paused' ? capBehind(run.pause_reason) : null;
  /** The cap he has typed when it is a valid raise above the saved one - what "Raise and continue" would save first. */
  const typedRaise = pausedOn ? (() => {
    const p = parseCapInput(pausedOn, caps[pausedOn]);
    return p.ok && p.value > status.budget[pausedOn] ? p.value : null;
  })() : null;
  const raised = typedRaise !== null;
  const canStart = !run || run.status === 'done' || run.status === 'failed';
  const canResume = !!run && (run.status === 'paused' || run.stalled);
  const unknownTypes = Array.from(new Set([
    ...(run?.unknown_doc_types ?? []), ...(status.runs.nightly?.unknown_doc_types ?? []),
  ]));

  return (
    <Card className="mt-3" data-testid="ceqa-header">
      <CardContent className="py-3 text-[13px] text-gray-700">
        {/* ⛔ RULING (I3): a refresh that failed keeps the last status that loaded on screen, and says it is that. */}
        {statusError && (
          <div data-testid="ceqa-header-error"
            className="mb-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[12.5px] text-amber-900">
            {`${asSentence(statusError)} The figures below are the last that loaded.`}
          </div>
        )}
        <div data-testid="ceqa-spend" className="font-medium text-gray-900">
          CEQA reading, last 30 days: {usd(status.spend30d)} of {usd(status.budget.cap_30d_usd)}
          <span className="font-normal text-gray-500"> (developer research included)</span>
        </div>
        <div data-testid="ceqa-load-line" className="mt-1">{loadLine(run, status.budget)}</div>
        <div data-testid="ceqa-nightly-line" className="mt-0.5 text-gray-500">
          {nightlyLine(status.runs.nightly, status.nightlyEnabled)}
        </div>
        <div data-testid="ceqa-public-line" className="mt-0.5 text-gray-500">
          Public projects are not leads: {status.counts.public} read and left off this list.
        </div>
        {unknownTypes.length > 0 && (
          <div data-testid="ceqa-unknown-types" className="mt-0.5 text-amber-800">
            New document types seen: {unknownTypes.join(', ')} - they show as Other filing until the map names them.
          </div>
        )}
        {status.unreadable.length > 0 && (
          <div className="mt-1">
            {/* ⛔ Never dropped (spec 6.4): counted by the control that hides them, each with its reason. */}
            <button type="button" data-testid="ceqa-unreadable-toggle" onClick={() => setShowUnreadable((v) => !v)}
              className="text-blue-700 hover:underline">
              {`${status.unreadable.length} couldn't be read - ${showUnreadable ? 'hide' : 'show'}`}
            </button>
            {showUnreadable && (
              <ul data-testid="ceqa-unreadable-list" className="mt-1 space-y-0.5 text-[12.5px] text-gray-600">
                {status.unreadable.map((u) => (
                  <li key={u.portal_url}>
                    <a href={u.portal_url} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                      {u.title ?? 'Untitled filing'}
                    </a>
                    {u.city ? `, ${u.city}` : ''} · received {u.received} · {u.reason ?? 'no reason recorded'}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {isAdmin && (
          <div data-testid="ceqa-admin" className="mt-2 border-t border-gray-100 pt-2">
            <div className="flex flex-wrap items-end gap-2">
              {CAP_KEYS.map((key) => (
                <label key={key} className="text-[12px] text-gray-600">
                  {CAP_LABEL[key]} ($)
                  <input data-testid={`ceqa-${key}`} inputMode="decimal" value={caps[key]}
                    onChange={(e) => setCaps((c) => ({ ...c, [key]: e.target.value }))}
                    className="ml-1 w-20 rounded border border-gray-300 px-1.5 py-0.5 text-[13px]" />
                </label>
              ))}
              <button type="button" data-testid="ceqa-save-caps" onClick={() => void saveCaps()} disabled={busy !== null}
                className="rounded border border-gray-300 px-2.5 py-1 text-[13px] text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                Save caps
              </button>
              <label className="flex items-center gap-1 text-[12px] text-gray-600">
                <input type="checkbox" data-testid="ceqa-checkpoint" checked={status.budget.checkpoint_first_month}
                  disabled={busy !== null} onChange={(e) => void setCheckpoint(e.target.checked)} />
                Stop after month one for a yes
              </label>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {canStart && (
                <button type="button" data-testid="ceqa-start" onClick={() => void start()} disabled={busy !== null}
                  className="rounded border border-violet-300 bg-violet-50 px-3 py-1.5 text-[13px] font-medium text-violet-900 hover:bg-violet-100 disabled:opacity-50">
                  Start the 12-month load
                </button>
              )}
              {/*
                ⛔ RULING (I1): a paused or stalled load ALWAYS offers plain Continue. Continuing under a cap that still
                   binds spends nothing - the caps are checked before every call - it simply pauses again, named. Without
                   it a cap already raised by "Save caps", a raise whose continue was refused (a nightly reading), or a
                   30-day spend that has since rolled back left only a disabled button: a dead end.
              */}
              {canResume && (
                <button type="button" data-testid="ceqa-continue" onClick={() => void resume(false)} disabled={busy !== null}
                  className="rounded border border-violet-300 bg-violet-50 px-3 py-1.5 text-[13px] font-medium text-violet-900 hover:bg-violet-100 disabled:opacity-50">
                  Continue
                </button>
              )}
              {/* A typed raise, saved BEFORE the load continues (one press). */}
              {canResume && pausedOn && (<>
                <button type="button" data-testid="ceqa-raise-continue" onClick={() => void resume(true)}
                  disabled={busy !== null || !raised}
                  className="rounded border border-violet-300 bg-violet-50 px-3 py-1.5 text-[13px] font-medium text-violet-900 hover:bg-violet-100 disabled:opacity-50">
                  Raise and continue
                </button>
                {/*
                  ⛔ RULING (N2): the two buttons told apart in FIGURES whenever a cap paused the load - a typed raise
                     included, which plain Continue does not save (the next status read writes the saved cap back).
                */}
                <span data-testid="ceqa-resume-note" className="text-[12px] text-gray-500">
                  {typedRaise !== null
                    ? `Continue keeps the ${CAP_LABEL[pausedOn].toLowerCase()} at ${usd(status.budget[pausedOn])}; Raise and continue saves ${usd(typedRaise)} first.`
                    : `Continue keeps the ${CAP_LABEL[pausedOn].toLowerCase()} at ${usd(status.budget[pausedOn])}, where it may pause again - type a higher cap to raise it as you continue.`}
                </span>
              </>)}
            </div>
          </div>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button type="button" data-testid="ceqa-refresh" onClick={() => void refresh()} disabled={refreshing}
            className="text-[12.5px] text-blue-700 hover:underline disabled:opacity-60">
            {refreshing ? 'Refreshing...' : 'Refresh status'}
          </button>
          {error && <span data-testid="ceqa-action-error" className="text-[12.5px] text-red-700">{error}</span>}
        </div>
      </CardContent>
    </Card>
  );
}
